/* i18n-ignore: interne adminpagina, alleen NL */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Eye, EyeOff, Pencil, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { adminPostsQuery } from "@/lib/blog-queries";
import { setPostPublished } from "@/lib/blog.functions";
import { AdminNav } from "@/components/admin/AdminNav";

export const Route = createFileRoute("/_authenticated/admin/artikels/")({
  staticData: { sitemap: false },
  head: () => ({ meta: [{ title: "Artikels beheren — Gopro Projects" }, { name: "robots", content: "noindex" }] }),
  component: AdminArticlesPage,
});

function AdminArticlesPage() {
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useQuery(adminPostsQuery);

  const toggle = useMutation({
    mutationFn: (vars: { id: string; published: boolean }) => setPostPublished({ data: vars }),
    onSuccess: (_res, vars) => {
      toast.success(vars.published ? "Artikel staat online." : "Artikel staat op concept.");
      void queryClient.invalidateQueries({ queryKey: ["blog"] });
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : "Wijzigen mislukt."),
  });

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-10 sm:px-6">
      <AdminNav />
      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight sm:text-3xl">Artikels</h1>
          <p className="mt-1 text-sm text-muted-foreground">Schrijf, bewerk en publiceer de artikels van de blog.</p>
        </div>
        <Button asChild>
          <Link to="/admin/artikels/$slug" params={{ slug: "nieuw" }}>
            <Plus className="mr-2 h-4 w-4" /> Nieuw artikel
          </Link>
        </Button>
      </div>

      {isLoading && (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-20 w-full rounded-xl" />
          ))}
        </div>
      )}

      {isError && (
        <Card>
          <CardContent className="p-6 text-sm text-destructive">
            De artikels konden niet geladen worden. Herlaad de pagina of probeer later opnieuw.
          </CardContent>
        </Card>
      )}

      {data && data.length === 0 && (
        <Card>
          <CardContent className="p-6 text-sm text-muted-foreground">
            Er zijn nog geen artikels. Maak er een met de knop hierboven.
          </CardContent>
        </Card>
      )}

      <div className="space-y-3">
        {data?.map((post) => (
          <Card key={post.id}>
            <CardContent className="flex flex-wrap items-center gap-4 p-4">
              <img
                src={post.image}
                alt={post.imageAlt}
                width={112}
                height={63}
                loading="lazy"
                className="hidden aspect-[16/9] w-28 rounded-lg object-cover sm:block"
              />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant={post.published ? "default" : "secondary"}>
                    {post.published ? "Online" : "Concept"}
                  </Badge>
                  <span className="text-xs text-muted-foreground">{post.category}</span>
                  <span className="text-xs text-muted-foreground">{post.datePublished}</span>
                </div>
                <p className="mt-1 truncate font-semibold">{post.title}</p>
                <p className="truncate text-sm text-muted-foreground">/blog/{post.slug}</p>
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={toggle.isPending}
                  onClick={() => toggle.mutate({ id: post.id, published: !post.published })}
                >
                  {post.published ? <EyeOff className="mr-1 h-4 w-4" /> : <Eye className="mr-1 h-4 w-4" />}
                  {post.published ? "Offline halen" : "Publiceren"}
                </Button>
                <Button asChild size="sm">
                  <Link to="/admin/artikels/$slug" params={{ slug: post.slug }}>
                    <Pencil className="mr-1 h-4 w-4" /> Bewerken
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
